// Report ↔ Goblic bridge protocol (docs/plans/2026-10-02-rapporten-ontwerp.md
// §5). The report runs in a sandboxed iframe (opaque origin); the injected
// bridge script (`bridge-script.ts`) and the parent page talk over
// `postMessage` with these messages only.
//
// Parent page rules (web): accept a message only when `event.source ===
// iframe.contentWindow` AND the frame still shows the original srcdoc
// document (count `load` events: a second load means it navigated away —
// ignore everything from it and reset the frame), parse it with
// `ReportBridgeChildMessageSchema`, show a confirmation dialog before posting
// a submission, at most one open submission at a time, and always answer a
// `goblic:submit` with a `goblic:submit-result` carrying the same `id`.
// `goblic:get-last-submission` is answered with the viewer's own latest
// submission only (never someone else's), and only while the frame is the
// original document.

import { z } from 'zod'
import { REPORT_MAX_SUBMISSION_BYTES, reportByteLength } from './limits.js'

export const REPORT_BRIDGE_SUBMIT = 'goblic:submit'
export const REPORT_BRIDGE_SUBMIT_RESULT = 'goblic:submit-result'
export const REPORT_BRIDGE_HEIGHT = 'goblic:height'
export const REPORT_BRIDGE_UNLOAD = 'goblic:unload'
export const REPORT_BRIDGE_GET_LAST_SUBMISSION = 'goblic:get-last-submission'
export const REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT = 'goblic:get-last-submission-result'

/**
 * Bumped when the message shapes change incompatibly. Exposed as `goblic.bridgeVersion`.
 * 2: `goblic.lastSubmission` replaced by `goblic.getLastSubmission()`.
 */
export const REPORT_BRIDGE_PROTOCOL_VERSION = 2

/** Any JSON value (what survives `JSON.parse(JSON.stringify(x))`). */
export type ReportJsonValue = string | number | boolean | null | ReportJsonValue[] | { [key: string]: ReportJsonValue }

export const ReportJsonValueSchema: z.ZodType<ReportJsonValue> = z.lazy(() =>
  z.union([z.string(), z.number().finite(), z.boolean(), z.null(), z.array(ReportJsonValueSchema), z.record(ReportJsonValueSchema)]),
)

/** Submission data: JSON, ≤ 64 KB serialized. */
export const ReportSubmissionDataSchema = ReportJsonValueSchema.superRefine((value, ctx) => {
  const size = reportByteLength(JSON.stringify(value))
  if (size > REPORT_MAX_SUBMISSION_BYTES) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Submission is ${size} bytes; at most ${REPORT_MAX_SUBMISSION_BYTES} bytes are allowed` })
  }
})

/**
 * Report → parent: the report wants to send `data` to Goblic. `id` (set by
 * the bridge script; optional on the wire) correlates the answer — the
 * parent echoes it in `goblic:submit-result` when present.
 */
export const ReportBridgeSubmitMessageSchema = z.object({
  type: z.literal(REPORT_BRIDGE_SUBMIT),
  id: z.string().min(1).max(64).optional(),
  data: ReportSubmissionDataSchema,
})
export type ReportBridgeSubmitMessage = z.infer<typeof ReportBridgeSubmitMessageSchema>

/** Report → parent: the document's height in CSS pixels. */
export const ReportBridgeHeightMessageSchema = z.object({
  type: z.literal(REPORT_BRIDGE_HEIGHT),
  height: z.number().finite().min(0).max(1_000_000),
})
export type ReportBridgeHeightMessage = z.infer<typeof ReportBridgeHeightMessageSchema>

/**
 * Report → parent: the original document is going away (`pagehide`). Sent
 * by the bridge before any message of a document the frame navigates to,
 * so the parent can stop trusting the frame even before its next `load`.
 */
export const ReportBridgeUnloadMessageSchema = z.object({
  type: z.literal(REPORT_BRIDGE_UNLOAD),
})
export type ReportBridgeUnloadMessage = z.infer<typeof ReportBridgeUnloadMessageSchema>

/** Report → parent: asks for the viewer's own latest submission (`goblic.getLastSubmission()`). */
export const ReportBridgeGetLastSubmissionMessageSchema = z.object({
  type: z.literal(REPORT_BRIDGE_GET_LAST_SUBMISSION),
  id: z.string().min(1).max(64),
})
export type ReportBridgeGetLastSubmissionMessage = z.infer<typeof ReportBridgeGetLastSubmissionMessageSchema>

export const ReportBridgeChildMessageSchema = z.discriminatedUnion('type', [
  ReportBridgeSubmitMessageSchema,
  ReportBridgeHeightMessageSchema,
  ReportBridgeGetLastSubmissionMessageSchema,
  ReportBridgeUnloadMessageSchema,
])
export type ReportBridgeChildMessage = z.infer<typeof ReportBridgeChildMessageSchema>

/**
 * Parent → report: the outcome of the open `goblic:submit` (the parent
 * allows one at a time). `id` echoes the submit's id; without it, the
 * bridge applies the answer to its one open submission.
 */
export const ReportBridgeSubmitResultMessageSchema = z.discriminatedUnion('ok', [
  z.object({ type: z.literal(REPORT_BRIDGE_SUBMIT_RESULT), id: z.string().min(1).max(64).optional(), ok: z.literal(true) }),
  z.object({ type: z.literal(REPORT_BRIDGE_SUBMIT_RESULT), id: z.string().min(1).max(64).optional(), ok: z.literal(false), error: z.string().max(500) }),
])
export type ReportBridgeSubmitResultMessage = z.infer<typeof ReportBridgeSubmitResultMessageSchema>

/** The viewer's own latest submission to this report, as the report sees it. */
export const ReportLastSubmissionSchema = z.object({
  /** The report version it was sent from. */
  version: z.number().int().min(1),
  data: ReportJsonValueSchema,
  /** ISO timestamp. */
  createdAt: z.string(),
})
export type ReportLastSubmission = z.infer<typeof ReportLastSubmissionSchema>

/**
 * Parent → report: the answer to `goblic:get-last-submission` (same `id`).
 * `submission: null` = the viewer hasn't sent anything to this report.
 */
export const ReportBridgeGetLastSubmissionResultMessageSchema = z.discriminatedUnion('ok', [
  z.object({ type: z.literal(REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT), id: z.string().min(1).max(64), ok: z.literal(true), submission: ReportLastSubmissionSchema.nullable() }),
  z.object({ type: z.literal(REPORT_BRIDGE_GET_LAST_SUBMISSION_RESULT), id: z.string().min(1).max(64), ok: z.literal(false), error: z.string().max(500) }),
])
export type ReportBridgeGetLastSubmissionResultMessage = z.infer<typeof ReportBridgeGetLastSubmissionResultMessageSchema>

/** What `goblic.submit()` resolves with inside the report. */
export type ReportBridgeSubmitOutcome = { ok: true } | { ok: false; error: string }

/** The `window.goblic` object the bridge script installs (for report authors / typing). */
export interface ReportBridgeApi {
  submit(data: unknown): Promise<ReportBridgeSubmitOutcome>
  /**
   * The viewer's own latest submission to this report, or null (none, not
   * answered within 10 s, or the parent refused). Never rejects.
   */
  getLastSubmission(): Promise<ReportLastSubmission | null>
  readonly version: number
  readonly bridgeVersion: number
}
